import React from 'react';
import {Link, Typography} from '@mui/material';
import {useSearchParams} from 'react-router';

const renderReferenceLabel = (ref) => {
    if (!ref) return '(missing)';
    if (ref.docTitle) return ref.docTitle;
    if (ref.name) return ref.name;
    if (ref.label) return ref.label;
    if (ref.url) return ref.url;
    const clone = {...ref};
    delete clone.content;
    try {
        return JSON.stringify(clone);
    } catch (e) {
        return String(ref);
    }
};

const ReferencesList = ({references, getReferenceNumber}) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Sort references by citation number
    const sortedReferences = React.useMemo(() => {
        if (!references || !getReferenceNumber) return references || [];

        return [...references].sort((a, b) => {
            const numA = getReferenceNumber(a.localId) || 999999;
            const numB = getReferenceNumber(b.localId) || 999999;
            return numA - numB;
        });
    }, [references, getReferenceNumber]);

    const handleReferenceClick = (ref) => (e) => {
        // allow anchor navigation but manage search params
        if (e && e.preventDefault) e.preventDefault();
        const next = new URLSearchParams(searchParams);
        if (ref && ref.docId) {
            next.set('documentId', String(ref.docId));
            const segIndex = (ref && ref.start && typeof ref.start.segmentIndex !== 'undefined') ? ref.start.segmentIndex : 0;
            next.set('segmentIndex', segIndex);
        } else {
            // if there's no docId, remove the param
            next.delete('documentId');
        }
        setSearchParams(next, {replace: true});

        // Optionally, scroll to the clicked reference anchor if present
        try {
            const anchorId = `ref-${ref.referenceMarkId || ref.id || ''}`;
            const el = document.getElementById(anchorId);
            if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
        } catch (err) {
            // ignore
        }
    };

    return (
        <div id="ref-list">
            <Typography variant="h6" gutterBottom>
                References
            </Typography>

            {(sortedReferences && sortedReferences.length > 0) ? (
                <ol>
                    {sortedReferences.map((ref, i) => {
                        const uid = ref.referenceMarkId || ref.id || `ref-${i}`;
                        const anchorId = `ref-${uid}`;
                        return (
                            <li key={`ref-${i}`} id={anchorId} style={{marginBottom: 8}}>
                                <a href={`#${anchorId}`} onClick={handleReferenceClick(ref)}
                                   style={{
                                       color: 'inherit',
                                       textDecoration: 'underline',
                                       textDecorationStyle: 'dotted'
                                   }}>
                                    <Typography>{renderReferenceLabel(ref)}</Typography>
                                </a>
                                <Link
                                    href={ref.docUrl || ref.url || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    color="text.secondary"
                                    variant="caption"
                                >
                                    {ref.docUrl}
                                </Link>
                            </li>
                        );
                    })}
                </ol>
            ) : (
                <Typography color="text.secondary">No references found for this report.</Typography>
            )}
        </div>
    );
};

export default ReferencesList;
